import type { ReactNode } from "react";
import React, { createContext, useContext, useState, useCallback } from "react";

import axios, { type AxiosResponse } from "axios";

import type {
  CompanyList,
  ProviderDirectoryCompaniesList200Response,
} from "~/src/types";

export interface CompanyDataProps {
  results: CompanyList[];
  loading?: boolean;
  error?: string | null;
  count?: number;
}

interface CompanyContextType {
  companyData: CompanyDataProps;
  fetchCompanies: (searchParams?: string) => void;
  companyResults: CompanyList[];
  setCompanyData: (data: CompanyDataProps) => void;
  setCompanyResults: (results: CompanyList[]) => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const CompanyProvider = ({ children }: { children: ReactNode }) => {
  const [companyData, setCompanyData] = useState<CompanyDataProps>({
    results: [],
    loading: true,
    error: null,
  });

  const fetchCompanies = useCallback((searchParams: string = "") => {
    setCompanyData((prev) => {
      return {
        ...prev,
        loading: true,
      };
    });

    axios
      .get(
        `provider_directory/companies${searchParams.startsWith("?") ? "" : "?"}${searchParams}`,
      )
      .then((res: AxiosResponse<ProviderDirectoryCompaniesList200Response>) => {
        setCompanyData({
          results: res.data.result?.results || [],
          count: res.data.result?.count || 0,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        console.error(err);
        setCompanyData({
          results: [],
          loading: false,
          error: "Something went wrong loading companies",
        });
      });
  }, []);

  const setCompanyDataFunc = useCallback((data: CompanyDataProps) => {
    setCompanyData((prev) => {
      return {
        ...prev,
        ...data,
      };
    });
  }, []);

  const setCompanyResults = useCallback((results: CompanyList[]) => {
    setCompanyData((prev) => {
      return {
        ...prev,
        results,
        loading: false,
      };
    });
  }, []);

  const contextValue: CompanyContextType = {
    companyData,
    fetchCompanies,
    companyResults: companyData.results,
    setCompanyData: setCompanyDataFunc,
    setCompanyResults,
  };

  return (
    <CompanyContext.Provider value={contextValue}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompanies = () => {
  const context = useContext(CompanyContext);

  if (context === undefined) {
    throw new Error("useCompanies must be used within a CompanyContext");
  }

  return context;
};
